import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import { API_ENDPOINTS } from '../../config/api'
import { Button } from '../ui/Button'
import { fetchEnterpriseHomeSummary, type EnterpriseHomeSummary } from '../../services/enterpriseOperationalUxApi'
import { recomputeExecutiveGovernance } from '../../services/executiveGovernanceApi'
import { featureFlags } from '../../config/featureFlags'
import {
  normalizeStability,
  pressureBand,
  pressureColor,
  stabilityColor,
} from '../../utils/operationalUx'
import type { HomeProblemsBreakdown } from '../../lib/assetsMonitoring'
import OperationalRuntimeTimeline from './context/OperationalRuntimeTimeline'
import OperationalBlastRadiusPanel from './context/OperationalBlastRadiusPanel'
import HomeExecutiveKpiPanels, { type ExecutiveSummaryData } from '../home/HomeExecutiveKpiPanels'
import styles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

const HOME_TIMELINE_PREF_KEY = 'defendsphere.home.showRuntimeTimeline'

function readShowRuntimeTimeline(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(HOME_TIMELINE_PREF_KEY) === 'true'
}

type Props = {
  totalSecurityHealth?: {
    healthPercent: number | null
    statusText: string
    statusColor: string
    compliancePercent: number
    problems: HomeProblemsBreakdown
    assessmentWarning?: string | null
    showPercent?: boolean
  } | null
}

export default function HomeEnterpriseOperationalCenter({
  totalSecurityHealth,
}: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<EnterpriseHomeSummary | null>(null)
  const [recomputing, setRecomputing] = useState(false)
  const [showRuntimeTimeline, setShowRuntimeTimeline] = useState(readShowRuntimeTimeline)
  const [executiveSummary, setExecutiveSummary] = useState<ExecutiveSummaryData | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchEnterpriseHomeSummary([])
      setSummary(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load operational summary')
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const loadExecutiveKpis = async () => {
      if (!token) {
        setExecutiveSummary(null)
        return
      }
      try {
        const res = await fetch(`${API_ENDPOINTS.GRC_EXECUTIVE_SUMMARY}?includeDiagnostics=true`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const json = res.ok ? await res.json().catch(() => ({})) : {}
        setExecutiveSummary((json?.data || null) as ExecutiveSummaryData | null)
      } catch {
        setExecutiveSummary(null)
      }
    }
    void loadExecutiveKpis()
  }, [token])

  const executiveKpis = useMemo(() => {
    if (!executiveSummary) return null
    const vulns = executiveSummary.vulnerabilities || {}
    const vulnerabilitiesTotal =
      Number(vulns.critical || 0) +
      Number(vulns.high || 0) +
      Number(vulns.medium || 0) +
      Number(vulns.low || 0)
    const overall =
      executiveSummary.compliance?.overallCompliance ??
      executiveSummary.overallCompliance ??
      executiveSummary.readinessScore ??
      0
    return {
      executiveOpenRisks: Number(executiveSummary.executiveOpenRisks || 0),
      openComplianceGaps: Number(
        executiveSummary.openComplianceGaps ??
          executiveSummary.openComplianceGapsBreakdown?.total ??
          executiveSummary.portfolioSummary?.openComplianceGaps ??
          0,
      ),
      internalOperationalObservations: Number(
        executiveSummary.internalOperationalObservations ??
          executiveSummary.complianceGapObservations ??
          0,
      ),
      openFindings: Number(executiveSummary.openFindings || 0),
      overdueRemediation: Number(executiveSummary.overdueRemediation || 0),
      overallCompliance: overall,
      verifiedCompliance: Number(
        executiveSummary.compliance?.verifiedCompliance ?? executiveSummary.verifiedCompliance ?? 0,
      ),
      readinessScore: Number(
        executiveSummary.compliance?.readinessScore ?? executiveSummary.readinessScore ?? overall,
      ),
      failedControls: Number(executiveSummary.failedControls || 0),
      openRecommendations: Number(executiveSummary.openRecommendations || 0),
      cisReadiness: Number(executiveSummary.cisReadiness || 0),
      isoReadiness: Number(executiveSummary.isoReadiness || 0),
      vulnerabilitiesTotal,
      vulnerabilitiesCritical: Number(vulns.critical || 0),
    }
  }, [executiveSummary])

  const posture = summary?.executive
  const stability = normalizeStability(posture?.certificationRiskLevel)
  const pressure = pressureBand(posture?.overallOperationalPressure)

  const mergedTotalSecurityHealth = useMemo(() => {
    if (!executiveKpis && !totalSecurityHealth) return null
    // SEC-004.3F: do not substitute compliance portfolio readiness for vulnerability health
    const vulnShow = totalSecurityHealth?.showPercent !== false
    const vulnPct = totalSecurityHealth?.healthPercent
    const portfolioReadiness = executiveSummary?.portfolioSummary?.portfolioReadiness
    const compliancePct =
      portfolioReadiness ??
      executiveKpis?.overallCompliance ??
      executiveKpis?.readinessScore ??
      0
    const healthPercent =
      vulnShow && vulnPct != null ? Number(vulnPct) : vulnShow === false ? null : Number(compliancePct)
    return {
      healthPercent: healthPercent ?? 0,
      statusText:
        totalSecurityHealth?.statusText ||
        (Number(healthPercent ?? 0) >= 85 ? 'Healthy' : Number(healthPercent ?? 0) >= 40 ? 'Moderate' : 'At risk'),
      statusColor:
        totalSecurityHealth?.statusColor ||
        (Number(healthPercent ?? 0) >= 85 ? '#34d399' : Number(healthPercent ?? 0) >= 40 ? '#fbbf24' : '#f87171'),
      compliancePercent: Number(compliancePct),
      assessmentWarning: totalSecurityHealth?.assessmentWarning || null,
      showPercent: vulnShow && healthPercent != null,
      problems: totalSecurityHealth?.problems || {
        total:
          (executiveKpis?.executiveOpenRisks || 0) +
          (executiveKpis?.failedControls || 0) +
          (executiveKpis?.vulnerabilitiesCritical || 0),
        vulnerabilities: executiveKpis?.vulnerabilitiesCritical || 0,
        risks: executiveKpis?.executiveOpenRisks || 0,
        controls: executiveKpis?.failedControls || 0,
      },
    }
  }, [executiveKpis, executiveSummary?.portfolioSummary, totalSecurityHealth])
  const topStandards = posture?.pressure?.mostDegradedStandards?.slice(0, 4) || []
  const storyLine = posture?.explanations?.lines?.[0]?.message

  const handleRecompute = async () => {
    if (!featureFlags.EXECUTIVE_GOVERNANCE_V1) return
    setRecomputing(true)
    try {
      await recomputeExecutiveGovernance()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recompute failed')
    } finally {
      setRecomputing(false)
    }
  }

  return (
    <div
      className={`${styles.workspace} ${styles.onDarkSurface}`}
      data-testid="home-enterprise-operational-center"
    >
      <section className={styles.layer}>
        <p className={styles.layerLabel}>
          {t('enterpriseOperationalUx.layer1', 'Layer 1 — Executive Operational Posture')}
        </p>
        <div className={styles.posturePanel}>
          <h2 className={styles.postureTitle}>
            {t('enterpriseOperationalUx.executiveOperationalCenter', 'Executive Operational Governance')}
          </h2>
          <p className={styles.postureSubtitle}>
            {t(
              'enterpriseOperationalUx.executiveOperationalCenterHint',
              'Pressure-first operational view — certification stability, governance risk, and runtime degradation.',
            )}
          </p>
          {loading ? (
            <p className={styles.emptyState}>{t('common.loading', 'Loading…')}</p>
          ) : null}
          {error ? <p className={styles.emptyState} role="alert">{error}</p> : null}
          <HomeExecutiveKpiPanels
            data={executiveSummary}
            healthStatus={
              mergedTotalSecurityHealth
                ? {
                    text: mergedTotalSecurityHealth.statusText,
                    color: mergedTotalSecurityHealth.statusColor,
                    overallScorePercent: mergedTotalSecurityHealth.healthPercent,
                  }
                : null
            }
          />
          <div style={{ marginTop: 12 }}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                const next = !showRuntimeTimeline
                setShowRuntimeTimeline(next)
                window.localStorage.setItem(HOME_TIMELINE_PREF_KEY, next ? 'true' : 'false')
              }}
            >
              {showRuntimeTimeline
                ? t('home.kpi.hideTimeline', 'Hide runtime timeline')
                : t('home.kpi.showTimeline', 'Show runtime timeline')}
            </Button>
          </div>
          {!loading && !posture && !summary?.hasExecutiveBaseline && !executiveKpis ? (
            <p className={styles.emptyState}>
              {t(
                'enterpriseOperationalUx.executiveKpisEmpty',
                'Executive KPIs will appear here once compliance and remediation data is available for your organization.',
              )}
            </p>
          ) : null}
          {posture ? (
            <>
              <div className={styles.postureGrid}>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('executiveGovernance.certificationStability', 'Certification Stability')}
                  </p>
                  <p className={styles.postureMetricValue} style={{ color: stabilityColor(stability) }}>
                    {stability}
                  </p>
                </div>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('executiveGovernance.governanceInstability', 'Governance Instability')}
                  </p>
                  <p className={styles.postureMetricValue}>
                    {posture.instability?.band || posture.governanceStability || '—'}
                  </p>
                </div>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('executiveGovernance.readinessTrajectory', 'Readiness Trajectory')}
                  </p>
                  <p className={styles.postureMetricValue}>{posture.readinessTrajectory || '—'}</p>
                </div>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('executiveGovernance.operationalPressure', 'Operational Pressure')}
                  </p>
                  <p className={styles.postureMetricValue} style={{ color: pressureColor(pressure) }}>
                    {pressure} · {posture.overallOperationalPressure ?? 0}
                  </p>
                </div>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('enterpriseOperationalUx.degradedWorkflows', 'Degraded workflows')}
                  </p>
                  <p className={styles.postureMetricValue}>{posture.unstableWorkflowCount ?? 0}</p>
                </div>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('enterpriseOperationalUx.criticalBlockers', 'Critical blockers')}
                  </p>
                  <p className={styles.postureMetricValue}>{posture.topBlockers ?? posture.blockerConcentration ?? 0}</p>
                </div>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('executiveGovernance.degradedControls', 'degraded controls')}
                  </p>
                  <p className={styles.postureMetricValue}>{posture.degradedControlCount ?? 0}</p>
                </div>
                <div className={styles.postureMetric}>
                  <p className={styles.postureMetricLabel}>
                    {t('enterpriseOperationalUx.staleEvidence', 'Stale evidence')}
                  </p>
                  <p className={styles.postureMetricValue}>{posture.staleEvidenceCount ?? 0}</p>
                </div>
              </div>
              {storyLine ? <p className={styles.postureStory}>{storyLine}</p> : null}
              {topStandards.length > 0 ? (
                <p className={styles.postureStory} style={{ borderLeftColor: '#60a5fa' }}>
                  {t('executiveGovernance.pressureConcentration', 'Pressure Concentration')}:{' '}
                  {topStandards.map((s) => `${s.standardCode} (${s.totalPressure})`).join(', ')}
                </p>
              ) : null}
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {featureFlags.EXECUTIVE_GOVERNANCE_V1 ? (
                  <Button type="button" size="small" onClick={() => void handleRecompute()} disabled={recomputing}>
                    {recomputing
                      ? t('common.loading', 'Loading…')
                      : t('executiveGovernance.recompute', 'Recompute posture')}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  onClick={() => navigate('/compliance-center/overview')}
                >
                  {t('complianceCenterUi.nav.overview', 'Compliance Overview')}
                </Button>
              </div>
            </>
          ) : null}
          {mergedTotalSecurityHealth ? (
            <p className={styles.postureStory}>
              {t(
                'home.totalSecurityHealth.story',
                'Overall health {percent}%. Compliance {compliance}%. Open problems: {vuln} vulnerabilities, {risks} risks, {failed} failed controls.',
                {
                  percent: String(Math.round(mergedTotalSecurityHealth.healthPercent)),
                  compliance: String(Math.round(mergedTotalSecurityHealth.compliancePercent)),
                  vuln: String(mergedTotalSecurityHealth.problems.vulnerabilities),
                  risks: String(mergedTotalSecurityHealth.problems.risks),
                  failed: String(mergedTotalSecurityHealth.problems.controls),
                },
              )}
            </p>
          ) : null}
        </div>
      </section>

      {summary?.blastRadius?.impacts?.length ? (
        <section className={styles.layer}>
          <p className={styles.layerLabel}>
            {t('enterpriseOperationalUx.layerBlast', 'Shared control blast radius')}
          </p>
          <div className={styles.blastPanel}>
            <h3 className={styles.sectionTitleDark} style={{ margin: 0 }}>
              {t('executiveGovernance.blastRadius', 'Blast Radius')}
            </h3>
            <OperationalBlastRadiusPanel
              hits={(summary.blastRadius.impacts || []).slice(0, 5).map((hit) => ({
                controlCode: hit.controlCode,
                summary: hit.summary,
                impactedStandards: hit.impactedStandards || [],
                standardCount: hit.standardCount,
                systemCount: hit.systemCount,
                severity: 'MODERATE',
              }))}
              mode="expanded"
            />
          </div>
        </section>
      ) : null}

      {featureFlags.COMPLIANCE_RUNTIME_V1 && showRuntimeTimeline ? (
        <section className={styles.layer}>
          <p className={styles.layerLabel}>
            {t('enterpriseOperationalUx.layerTimeline', 'Runtime operational timeline')}
          </p>
          <div className={styles.timeline}>
            <h3 className={styles.sectionTitleDark} style={{ margin: 0 }}>
              {t('enterpriseOperationalUx.runtimeTimeline', 'Operational timeline')}
            </h3>
            <OperationalRuntimeTimeline
              events={(summary?.timeline || []).map((item) => ({
                id: item.id,
                at: item.at,
                eventType: item.eventType,
                severity: item.severity,
                summary: item.summary,
                relatedStandardCode: item.relatedStandardCode,
              }))}
              mode="full"
              maxItems={12}
            />
          </div>
        </section>
      ) : null}
    </div>
  )
}
